import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Clock3, ShieldCheck, Workflow } from "lucide-react";
import { SERVICES } from "@/data/services";

/** Above-footer contextual section. Shows different content per route. */
export default function PreFooterCTA() {
  const { pathname } = useLocation();

  let title = "Outsource Your Next Drafting Project";
  let copy =
    "From boundary retracements to full ALTA packages, our survey techies plug into your firm's workflow within 24 hours.";
  let badge = "Ready when you are";

  if (pathname.startsWith("/services/")) {
    const slug = pathname.split("/")[2];
    const s = SERVICES.find((x) => x.slug === slug);
    if (s) {
      title = `Need ${s.name} drafting support?`;
      copy = s.hero;
      badge = `${s.emoji} ${s.name}`;
    }
  } else if (pathname === "/services") {
    title = "11 production-grade CAD services. One trusted partner.";
    copy =
      "We staff teams behind the scenes for surveying firms — your brand stays front-and-center while we produce the drawings.";
    badge = "Full-service production";
  } else if (pathname === "/survey-techies") {
    title = "Embed a senior land-survey CAD techie into your workflow";
    copy =
      "Dedicated drafters trained on Civil 3D, AutoCAD, MicroStation, Trimble Business Center, and Carlson — billed by hour, day, or project.";
    badge = "Land-survey talent";
  } else if (pathname === "/cad-techies") {
    title = "Architectural · Structural · MEP · Mechanical CAD techies, on tap";
    copy =
      "Tap into our non-survey CAD bench — Revit, SolidWorks, Tekla, AutoCAD, Inventor, MicroStation. Hourly, daily or per project.";
    badge = "Design & detailing talent";
  } else if (pathname === "/about") {
    title = "A drafting partner that thinks like a surveyor";
    copy =
      "Built by licensed surveyors and senior CAD leads — we speak your standards, blocks, and templates.";
    badge = "Why firms switch to us";
  } else if (pathname === "/contact") {
    title = "Prefer email? Send us the project brief.";
    copy = "Drop a note at triaxisxyzsolutions@gmail.com or WhatsApp +91 80836 91588 — we reply within one business day, including weekends in busy seasons.";
    badge = "Direct contact";
  }

  return (
    <section
      data-testid="prefooter-cta"
      className="relative bg-white border-t border-slate-200"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 mono text-[10px] tracking-[0.22em] uppercase text-emerald-700 border border-emerald-700/30 bg-emerald-50 px-2 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" /> {badge}
          </div>
          <h2 className="display mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900 max-w-3xl">
            {title}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">{copy}</p>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="prefooter-cta-button"
              className="inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-5 py-3"
            >
              Start a Project <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/services"
              data-testid="prefooter-services-button"
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-5 py-3 bg-white"
            >
              Browse All Services
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-3">
          <Stat icon={<Clock3 className="h-4 w-4" />} label="Turnaround" value="24–72 hrs" />
          <Stat icon={<ShieldCheck className="h-4 w-4" />} label="QC Layers" value="3-tier review" />
          <Stat icon={<Workflow className="h-4 w-4" />} label="Workflow" value="ISO-grade" />
        </div>
      </div>
    </section>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="border border-slate-200 p-4 flex items-center gap-3 bg-paper-dark/40">
      <span className="inline-flex h-9 w-9 items-center justify-center bg-orange-survey text-white rounded-sm">
        {icon}
      </span>
      <div>
        <div className="mono text-[10px] tracking-[0.18em] uppercase text-slate-500">{label}</div>
        <div className="text-base font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
}
